(() => {
  const CM_PER_INCH = 2.54;

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const normalized = String(value)
      .trim()
      .replace(',', '.')
      .replace(/[^\d.-]/g, '');

    if (!normalized) return null;

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const roundTo = (value, decimals = 1) => {
    if (!Number.isFinite(value)) return null;
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };

  const formatMeasure = (value, unit) => {
    const parsed = parseNumber(value);
    if (parsed === null) return '';

    const rounded = roundTo(parsed, 1);
    if (rounded === null) return '';

    if (Number.isInteger(rounded)) return `${rounded} ${unit}`;
    return `${rounded.toFixed(1).replace(/\.0$/, '')} ${unit}`;
  };

  const toCm = (value, unit) => {
    const parsed = parseNumber(value);
    if (parsed === null) return null;
    return unit === 'in' ? parsed * CM_PER_INCH : parsed;
  };

  const normalizeToken = (value) => String(value || '').trim().toLowerCase();
  const normalizeSizeToken = (value) => String(value || '').trim().toUpperCase();
  const normalizeWaistMode = (value, fallback = 'true') => {
    const token = normalizeToken(value);
    return ['slim', 'true', 'loose'].includes(token) ? token : fallback;
  };

  const compareBySortOrder = (a, b) => {
    const orderA = parseNumber(a?.sortOrder) ?? 0;
    const orderB = parseNumber(b?.sortOrder) ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return normalizeToken(a?.sizeLabel || a?.lengthLabel).localeCompare(
      normalizeToken(b?.sizeLabel || b?.lengthLabel)
    );
  };

  const getProductInfo = (element) => element.closest('product-info') || document;

  const getProductForm = (element) => {
    const root = getProductInfo(element);
    const formId = element?.dataset?.productFormId;

    if (formId) {
      const byId = root?.querySelector?.(`#${formId}`) || document.getElementById(formId);
      if (byId) return byId;
    }

    return (
      root?.querySelector?.('form[data-type="add-to-cart-form"]') ||
      root?.querySelector?.('form[action*="/cart/add"]') ||
      null
    );
  };

  const getVariantSelects = (element) => {
    const root = getProductInfo(element);
    const form = getProductForm(element);

    if (form) {
      const insideForm = form.querySelector('variant-selects');
      if (insideForm) return insideForm;

      const all = Array.from(root.querySelectorAll('variant-selects'));
      if (all.length === 1) return all[0];
      if (!all.length) return null;

      const sameForm = all.find((vs) => vs.closest('form') === form);
      if (sameForm) return sameForm;

      return all[0] || null;
    }

    return root.querySelector('variant-selects');
  };

  const getOptionContainer = (element, matcher) => {
    const variantSelects = getVariantSelects(element);
    if (!variantSelects) return null;

    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset.product-form__input'));
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
      if (matcher(legend)) return { type: 'radio', element: fieldset };
    }

    const dropdowns = Array.from(variantSelects.querySelectorAll('.product-form__input--dropdown'));
    for (const dropdown of dropdowns) {
      const label = dropdown.querySelector('label')?.textContent?.trim().toLowerCase() || '';
      if (matcher(label)) return { type: 'select', element: dropdown };
    }

    return null;
  };

  const getNumericOptionValues = (control) => {
    if (!control) return [];

    if (control.type === 'radio') {
      const radios = Array.from(control.element.querySelectorAll('input[type="radio"]')).filter(
        (input) => !input.disabled && !input.classList.contains('disabled')
      );
      return [
        ...new Set(radios.map((input) => parseNumber(input.value)).filter((value) => value !== null)),
      ].sort((a, b) => a - b);
    }

    const select = control.element.querySelector('select');
    if (!select) return [];

    return [
      ...new Set(
        Array.from(select.options).map((option) => parseNumber(option.value)).filter((value) => value !== null)
      ),
    ].sort((a, b) => a - b);
  };

  const getWaistControl = (element) => {
    const strictMatchers = [
      (label) => label.includes('waist'),
      (label) => label.includes('vita'),
      (label) => label === 'w',
    ];

    for (const matcher of strictMatchers) {
      const control = getOptionContainer(element, matcher);
      if (control) return control;
    }

    const softMatchers = [
      (label) => label.includes('size'),
      (label) => label.includes('taglia'),
    ];

    for (const matcher of softMatchers) {
      const control = getOptionContainer(element, matcher);
      if (control && getNumericOptionValues(control).length >= 2) return control;
    }

    const variantSelects = getVariantSelects(element);
    if (!variantSelects) return null;

    const controls = [
      ...Array.from(variantSelects.querySelectorAll('fieldset.product-form__input')).map((fieldset) => ({
        type: 'radio',
        element: fieldset,
      })),
      ...Array.from(variantSelects.querySelectorAll('.product-form__input--dropdown')).map((dropdown) => ({
        type: 'select',
        element: dropdown,
      })),
    ];

    return controls.find((control) => getNumericOptionValues(control).length >= 2) || null;
  };

    const isWaistSelected = (element, waistValue) => {
    const normalizedTarget = normalizeSizeToken(waistValue);
    const targetNumeric = parseNumber(waistValue);

    if (!normalizedTarget && targetNumeric === null) return false;

    const matchesTarget = (rawValue) => {
      const normalizedValue = normalizeSizeToken(rawValue);

      if (normalizedTarget && normalizedValue === normalizedTarget) {
        return true;
      }

      return (
        targetNumeric !== null &&
        parseNumber(rawValue) === targetNumeric
      );
    };

    const control = getWaistControl(element);
    if (!control) return false;

    if (control.type === 'radio') {
      return Array.from(
        control.element.querySelectorAll('input[type="radio"]')
      ).some(
        (input) =>
          input.checked &&
          matchesTarget(input.value)
      );
    }

    const select = control.element.querySelector('select');

    return !!select && matchesTarget(select.value);
  };

  const selectWaist = (element, waistValue) => {
    const normalizedTarget = normalizeSizeToken(waistValue);
    const targetNumeric = parseNumber(waistValue);
    if (!normalizedTarget && targetNumeric === null) return false;

    const control = getWaistControl(element);
    if (!control) return false;

    const matchesTarget = (rawValue) => {
      const normalizedValue = normalizeSizeToken(rawValue);
      if (normalizedTarget && normalizedValue === normalizedTarget) return true;
      if (targetNumeric !== null && parseNumber(rawValue) === targetNumeric) return true;
      return false;
    };

    if (control.type === 'radio') {
      const radios = Array.from(control.element.querySelectorAll('input[type="radio"]'));
      const target = radios.find(
        (input) => !input.disabled && !input.classList.contains('disabled') && matchesTarget(input.value)
      );

      if (!target) return false;

      if (target.checked) return true;

      target.click();
      return true;
      }

    const select = control.element.querySelector('select');
    if (!select) return false;

    const targetOption = Array.from(select.options).find((option) => matchesTarget(option.value));
    if (!targetOption) return false;

    select.value = targetOption.value;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const getHemmingContainer = (element) =>
    getProductInfo(element).querySelector('[data-klsh-hemming]');

  const valuesMatch = (firstValue, secondValue) => {
    const first = `${firstValue || ''}`.trim();
    const second = `${secondValue || ''}`.trim();

    if (!first || !second) return false;
    if (first === second) return true;

    const firstNumber = parseNumber(first);
    const secondNumber = parseNumber(second);

    return (
      firstNumber !== null &&
      secondNumber !== null &&
      firstNumber === secondNumber
    );
  };

  const selectHem = (element, hem) => {
    const requested = `${hem || ''}`.trim();
    if (!requested) return false;

    const hemming = getHemmingContainer(element);

    if (!hemming?.klshHemming?.selectLength) {
      return false;
    }

    const selected =
      hemming.klshHemming.selectLength(requested);

    return valuesMatch(selected, requested);
  };

  const waitForHemSelection = (
    element,
    requestedLength,
    timeoutMs = 2500
  ) => {
    return new Promise((resolve) => {
      const start = Date.now();
      let matchingChecks = 0;

      const tick = () => {
        const hemming = getHemmingContainer(element);
        const api = hemming?.klshHemming || null;

        if (
          api?.selectLength &&
          api?.getSelectedLength
        ) {
          let selectedLength =
            api.getSelectedLength();

          if (
            !valuesMatch(
              selectedLength,
              requestedLength
            )
          ) {
            api.selectLength(requestedLength);
            selectedLength =
              api.getSelectedLength();
          }

          if (
            valuesMatch(
              selectedLength,
              requestedLength
            )
          ) {
            matchingChecks += 1;

            if (matchingChecks >= 3) {
              resolve(true);
              return;
            }
          } else {
            matchingChecks = 0;
          }
        } else {
          matchingChecks = 0;
        }

        if (Date.now() - start > timeoutMs) {
          resolve(false);
          return;
        }

        window.setTimeout(tick, 50);
      };

      tick();
    });
  };

    const getActiveMode = (group) => {
    const active = group?.querySelector('.klsh-fit-toggle__button.is-active')?.dataset.value;
    if (active) return active;
    if (group?.hasAttribute?.('data-klsh-fit-mode')) return 'true';
    return 'regular';
  };
  const setActiveMode = (group, value) => {
    if (!group) return;

    group.querySelectorAll('.klsh-fit-toggle__button').forEach((button) => {
      const active = button.dataset.value === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const setToggleButtonDisabled = (button, disabled) => {
    if (!button) return;
    button.disabled = !!disabled;
    button.classList.toggle('is-disabled', !!disabled);
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  const clearPredictorInputs = (predictor) => {
  const waistInput = predictor.querySelector('[data-klsh-fit-waist]');
  const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');

  [waistInput, inseamInput].forEach((input) => {
    if (!input) return;
    input.value = '';
    input.setAttribute('value', '');
    input.blur();
  });
};

const hasValidPredictorInputs = (predictor) => {
  const profile = getMeasureProfile(predictor);
  const waistInput = predictor.querySelector('[data-klsh-fit-waist]');
  const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');
  const inputUnit = getSelectedInputUnit(predictor);

  const waistValueCm = toCm(waistInput?.value, inputUnit);
  const inseamValueCm = toCm(inseamInput?.value, inputUnit);

  if (waistValueCm === null) return false;
 if (profileHasLengthChoices(profile) && inseamValueCm === null) return false;

  return true;
};

const setPredictorLockedState = (predictor, locked) => {
  const applyButton = predictor.querySelector('[data-klsh-fit-apply]');
  if (applyButton) {
    applyButton.disabled = locked;
  }
};

const resetPredictorState = (predictor) => {
  const profile = getMeasureProfile(predictor);
  const fitMode = predictor.querySelector('[data-klsh-fit-mode]');
  const lengthMode = predictor.querySelector('[data-klsh-length-mode]');
  const unitMode = predictor.querySelector('[data-klsh-fit-unit-mode]');

  clearPredictorInputs(predictor);

  if (fitMode) {
    setActiveMode(fitMode, normalizeWaistMode(profile?.predictor?.defaultWaistMode, 'true'));
  }
  if (lengthMode) setActiveMode(lengthMode, 'regular');
  if (unitMode) {
    setActiveMode(unitMode, profile.units.defaultInputUnit || 'cm');
  }
};

  const getVisualMap = (predictor) => {
    if (predictor.klshFitVisualMap) return predictor.klshFitVisualMap;

    const script = predictor.querySelector('[data-klsh-fit-visual-map]');
    if (!script) {
      predictor.klshFitVisualMap = {};
      return predictor.klshFitVisualMap;
    }

    try {
      predictor.klshFitVisualMap = JSON.parse(script.textContent);
    } catch (error) {
      predictor.klshFitVisualMap = {};
    }

    return predictor.klshFitVisualMap;
  };

  const normalizeLegacyCopyText = (value) =>
    String(value || '')
      .replace(/\bhem\b/gi, 'inseam')
      .replace(/\bOn the leg:\s*/gi, 'On the inseam: ')
      .replace(/\[\[\s*hem\s*\]\]/gi, '[[inseam]]');

  const getCopyMap = (predictor) => {
    if (predictor.klshFitCopyMap) return predictor.klshFitCopyMap;

    const defaults = {
      visualPlaceholder: 'Fit preview not available yet',
      summaryTemplate: '',
      waistSlimCopy: '',
      waistTrueCopy: '',
      waistRegularCopy: 'On the waist: we kept the closest waist for the intended fit.',
      waistLooseCopy: 'On the waist: we moved one size up for a looser fit.',
      hemRegularCopy: 'On the inseam: we matched the closest inseam to your current measurement.',
      hemCroppedCopy: 'On the inseam: we moved two step shorter for a cropped finish.',
      hemStackedCopy: 'On the inseam: we moved one step longer for a stacked finish.',
    };

    const script = predictor.querySelector('[data-klsh-fit-copy-map]');
    if (!script) {
      predictor.klshFitCopyMap = defaults;
      return predictor.klshFitCopyMap;
    }

    try {
      const parsed = JSON.parse(script.textContent);
      predictor.klshFitCopyMap = {
        ...defaults,
        ...parsed,
      };
    } catch (error) {
      predictor.klshFitCopyMap = defaults;
    }

    predictor.klshFitCopyMap.summaryTemplate = normalizeLegacyCopyText(predictor.klshFitCopyMap.summaryTemplate);
    predictor.klshFitCopyMap.hemRegularCopy = normalizeLegacyCopyText(predictor.klshFitCopyMap.hemRegularCopy);
    predictor.klshFitCopyMap.hemCroppedCopy = normalizeLegacyCopyText(predictor.klshFitCopyMap.hemCroppedCopy);
    predictor.klshFitCopyMap.hemStackedCopy = normalizeLegacyCopyText(predictor.klshFitCopyMap.hemStackedCopy);

    return predictor.klshFitCopyMap;
  };

  const applyTemplate = (template, replacements = {}) =>
    String(template || '').replace(/\[\[\s*(\w+)\s*\]\]/g, (match, token) => {
      return Object.prototype.hasOwnProperty.call(replacements, token) ? replacements[token] : '';
    });

  const getMeasureProfile = (predictor) => {
    if (predictor.klshFitMeasureProfile) return predictor.klshFitMeasureProfile;

    const script = predictor.querySelector('[data-klsh-fit-measure-profile]');
    const empty = {
      profile: null,
      predictor: {
        defaultWaistMode: 'true',
        waistModes: {
          slim: { label: 'Slim', offset: null },
          true: { label: 'True', offset: 0 },
          loose: { label: 'Loose', offset: null },
        },
      },
      units: { supported: ['cm', 'in'], calculationUnit: 'cm', defaultInputUnit: 'cm' },
      fitModes: ['slim', 'true', 'loose'],
      lengthModes: ['cropped', 'regular', 'stacked'],
      sizes: [],
      lengths: [],
    };

    if (!script) {
      predictor.klshFitMeasureProfile = empty;
      return predictor.klshFitMeasureProfile;
    }

    try {
      const parsed = JSON.parse(script.textContent);
        const sizes = Array.isArray(parsed?.sizes)
          ? parsed.sizes
              .map((size) => {
                const sizeLabel = size?.sizeLabel || '';
                const variantSizeValue = size?.variantSizeValue || sizeLabel || '';

                return {
                  sizeLabel,
                  variantSizeValue,
                  sortOrder: parseNumber(size?.sortOrder) ?? 0,

                  // Taglia nominale: W30 oppure 30 diventano entrambi 30
                  nominalWaist:
                    parseNumber(variantSizeValue) ??
                    parseNumber(sizeLabel),

                  // Misure reali del capo, conservate ma non usate come label
                  waistCm: parseNumber(size?.waistCm),
                  waistIn: parseNumber(size?.waistIn),
                };
              })
              .filter(
                (size) =>
                  size.sizeLabel &&
                  size.variantSizeValue &&
                  size.nominalWaist !== null
              )
              .sort((a, b) => {
                if (a.nominalWaist !== b.nominalWaist) {
                  return a.nominalWaist - b.nominalWaist;
                }

                return compareBySortOrder(a, b);
              })
          : [];

                const lengths = Array.isArray(parsed?.lengths)
                ? parsed.lengths
                    .map((length) => ({
                      lengthLabel: length?.lengthLabel || '',
                      inseamCm: parseNumber(length?.inseamCm),
                      inseamIn: parseNumber(length?.inseamIn),
                      sortOrder: parseNumber(length?.sortOrder) ?? 0,
                      isDefault:
                        length?.isDefault === true ||
                        normalizeToken(length?.isDefault) === 'true',
                    }))
                    .filter(
                      (length) =>
                        length.lengthLabel &&
                        (length.inseamCm !== null || length.inseamIn !== null)
                    )
                    .sort(compareBySortOrder)
                : [];

      predictor.klshFitMeasureProfile = {
        profile: parsed?.profile || null,
        predictor: {
          defaultWaistMode: normalizeWaistMode(parsed?.predictor?.defaultWaistMode, 'true'),
          waistModes: {
            slim: {
              label: parsed?.predictor?.waistModes?.slim?.label || 'Slim',
              offset: parseNumber(parsed?.predictor?.waistModes?.slim?.offset),
            },
            true: {
              label: parsed?.predictor?.waistModes?.true?.label || 'True',
              offset: parseNumber(parsed?.predictor?.waistModes?.true?.offset) ?? 0,
            },
            loose: {
              label: parsed?.predictor?.waistModes?.loose?.label || 'Loose',
              offset: parseNumber(parsed?.predictor?.waistModes?.loose?.offset),
            },
          },
        },
        units: {
          supported:
            Array.isArray(parsed?.units?.supported) && parsed.units.supported.length
              ? parsed.units.supported
              : ['cm', 'in'],
          calculationUnit: parsed?.units?.calculationUnit || 'cm',
          defaultInputUnit: parsed?.units?.defaultInputUnit || 'cm',
        },
        fitModes:
          Array.isArray(parsed?.fitModes) && parsed.fitModes.length
            ? parsed.fitModes.map((mode) => normalizeWaistMode(mode, '')).filter(Boolean)
            : ['slim', 'true', 'loose'],
        lengthModes: Array.isArray(parsed?.lengthModes) ? parsed.lengthModes : ['cropped', 'regular', 'stacked'],
        sizes,
        lengths,
      };
    } catch (error) {
      predictor.klshFitMeasureProfile = empty;
    }

    return predictor.klshFitMeasureProfile;
  };

  const profileHasLengthChoices = (profile) => {
    const lengthMode = normalizeToken(profile?.profile?.lengthMode || '');
    const waistOnlyModes = ['short', 'shorts', 'waist_only', 'waist-only', 'waist only', 'none', 'no_length', 'no-length'];

    if (waistOnlyModes.includes(lengthMode)) return false;
    return Array.isArray(profile?.lengths) && profile.lengths.length > 1;
  };

  const getSelectedInputUnit = (predictor) => {
    const profile = getMeasureProfile(predictor);
    const group = predictor.querySelector('[data-klsh-fit-unit-mode]');
    const selected = getActiveMode(group);
    if (profile.units.supported.includes(selected)) return selected;
    return profile.units.defaultInputUnit || 'cm';
  };

  const getRangeForType = (profile, type, unit) => {
    const items =
      type === 'waist'
        ? profile?.sizes
        : profile?.lengths;

    if (!Array.isArray(items) || !items.length) {
      return null;
    }

    const values =
      type === 'waist'
        ? items
            .map((item) =>
              parseNumber(item?.nominalWaist)
            )
            .filter((value) => value !== null)
            .map((value) =>
              unit === 'cm'
                ? value * CM_PER_INCH
                : value
            )
            .sort((a, b) => a - b)
        : items
            .map((item) =>
              parseNumber(
                unit === 'in'
                  ? item?.inseamIn
                  : item?.inseamCm
              )
            )
            .filter((value) => value !== null)
            .sort((a, b) => a - b);

    if (!values.length) return null;

    return {
      min: roundTo(values[0], 1),
      max: roundTo(
        values[values.length - 1],
        1
      ),
    };
  };

const formatInputValue = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `${roundTo(value, 1)}`.replace(/\.0$/, '');
};

const convertValueBetweenUnits = (
  value,
  fromUnit,
  toUnit
) => {
  const parsed = parseNumber(value);

  if (parsed === null) return '';
  if (fromUnit === toUnit) {
    return formatInputValue(parsed);
  }

  const converted =
    fromUnit === 'cm' && toUnit === 'in'
      ? parsed / CM_PER_INCH
      : fromUnit === 'in' && toUnit === 'cm'
        ? parsed * CM_PER_INCH
        : parsed;

  return formatInputValue(converted);
};

const convertPredictorInputsUnit = (
  predictor,
  fromUnit,
  toUnit
) => {
  if (!predictor || fromUnit === toUnit) return;

  const inputs = [
    predictor.querySelector('[data-klsh-fit-waist]'),
    predictor.querySelector('[data-klsh-fit-inseam]'),
  ];

  inputs.forEach((input) => {
    if (!input || input.value === '') return;

    const convertedValue =
      convertValueBetweenUnits(
        input.value,
        fromUnit,
        toUnit
      );

    if (!convertedValue) return;

    input.value = convertedValue;
    input.setAttribute('value', convertedValue);
  });
};

const applyRangeToInput = (input, range, unit) => {
  if (!input) return;

  input.placeholder = '';
  input.step = unit === 'in' ? '0.2' : '0.5';

  if (!range) {
    input.removeAttribute('min');
    input.removeAttribute('max');
    return;
  }

  input.min = formatInputValue(range.min);
  input.max = formatInputValue(range.max);
};

const updateRangeMeta = (predictor, type, range, unit) => {
  const node = predictor.querySelector(`[data-klsh-fit-range="${type}"]`);
  if (!node) return;

  node.textContent = range ? `${formatMeasure(range.min, unit)}–${formatMeasure(range.max, unit)}` : '';
};

const getCurrentInputRanges = (predictor) => {
  const profile = getMeasureProfile(predictor);
  const unit = getSelectedInputUnit(predictor);

  return {
    waist: getRangeForType(profile, 'waist', unit),
    inseam: getRangeForType(profile, 'inseam', unit),
  };
};

const clampInputToRange = (input, range) => {
  if (!input || !range || input.value === '') return false;

  const parsed = parseNumber(input.value);
  if (parsed === null) return false;

  const clamped = Math.min(range.max, Math.max(range.min, parsed));

  if (clamped !== parsed) {
    input.value = formatInputValue(clamped);
    input.setAttribute('value', input.value);
    return true;
  }

  return false;
};

const clampPredictorInputs = (predictor) => {
  const { waist, inseam } = getCurrentInputRanges(predictor);
  const waistInput = predictor.querySelector('[data-klsh-fit-waist]');
  const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');

  let changed = false;

  changed = clampInputToRange(waistInput, waist) || changed;
  changed = clampInputToRange(inseamInput, inseam) || changed;

  return changed;
};

  const updateUnitLabels = (predictor) => {
    const unit = getSelectedInputUnit(predictor);
    const profile = getMeasureProfile(predictor);

    predictor.querySelectorAll('[data-klsh-fit-current-unit]').forEach((node) => {
      node.textContent = unit;
    });

    const waistInput = predictor.querySelector('[data-klsh-fit-waist]');
    const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');

    const waistRange = getRangeForType(profile, 'waist', unit);
    const inseamRange = getRangeForType(profile, 'inseam', unit);

    applyRangeToInput(waistInput, waistRange, unit);
    applyRangeToInput(inseamInput, inseamRange, unit);

    updateRangeMeta(predictor, 'waist', waistRange, unit);
    updateRangeMeta(predictor, 'inseam', inseamRange, unit);
  };

  const getNearestItemIndex = (items, valueKey, targetValue, defaultIndex = 0) => {
    if (!items.length || targetValue === null) return -1;

    let bestIndex = defaultIndex;
    let bestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const candidate = parseNumber(item?.[valueKey]);
      if (candidate === null) return;

      const distance = Math.abs(candidate - targetValue);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
        return;
      }

      if (distance === bestDistance) {
        const currentBest = parseNumber(items[bestIndex]?.[valueKey]);
        const currentBestIsDefault = !!items[bestIndex]?.isDefault;
        const candidateIsDefault = !!item?.isDefault;

        if (candidateIsDefault && !currentBestIsDefault) {
          bestIndex = index;
          return;
        }

        if (!candidateIsDefault && !currentBestIsDefault && currentBest !== null && candidate < currentBest) {
          bestIndex = index;
        }
      }
    });

    return bestIndex;
  };

  const getWaistModesConfig = (profile) => ({
    slim: {
      label: profile?.predictor?.waistModes?.slim?.label || 'Slim',
      offset: parseNumber(profile?.predictor?.waistModes?.slim?.offset),
    },
    true: {
      label: profile?.predictor?.waistModes?.true?.label || 'True',
      offset: parseNumber(profile?.predictor?.waistModes?.true?.offset) ?? 0,
    },
    loose: {
      label: profile?.predictor?.waistModes?.loose?.label || 'Loose',
      offset: parseNumber(profile?.predictor?.waistModes?.loose?.offset),
    },
  });

  const formatWaistStepCopy = (offset, direction) => {
    const steps = Math.abs(parseNumber(offset) ?? 0);
    const label = `${steps} ${steps === 1 ? 'size' : 'sizes'}`;
    return `On the waist: we moved ${label} ${direction} for the selected fit.`;
  };

  const buildWaistCopy = (copy, waist) => {
    if (!waist) return '';

    const offset = parseNumber(waist.offsetApplied) ?? 0;

    if (offset === 0) {
      return (
        copy.waistTrueCopy ||
        copy.waistRegularCopy ||
        'On the waist: we kept the closest waist for the intended fit.'
      );
    }

    if (offset > 0) {
      if (offset === 1 && copy.waistLooseCopy) return copy.waistLooseCopy;
      return formatWaistStepCopy(offset, 'up');
    }

    if (offset === -1 && copy.waistSlimCopy) return copy.waistSlimCopy;
    return formatWaistStepCopy(offset, 'down');
  };

  const buildWaistResult = (sizes, baseIndex, fitMode, profile) => {
    if (!sizes.length || baseIndex < 0 || !sizes[baseIndex]) return null;

    const base = sizes[baseIndex];
    const waistModes = getWaistModesConfig(profile);
    const availability = {
      slim: false,
      true: false,
      loose: false,
    };
    const candidates = {};

    ['slim', 'true', 'loose'].forEach((mode) => {
      const offset = waistModes[mode].offset;
      if (offset === null) return;

      const selectedIndex = baseIndex + offset;
      const selected = sizes[selectedIndex] || null;
      if (!selected) return;

      availability[mode] = true;
      candidates[mode] = {
        offset,
        selectedIndex,
        selected,
      };
    });

    let activeMode = normalizeWaistMode(fitMode, 'true');
    if (!availability[activeMode]) {
      activeMode = availability.true ? 'true' : ['slim', 'loose'].find((mode) => availability[mode]) || 'true';
    }

    const activeCandidate = candidates[activeMode];
    if (!activeCandidate) return null;

    return {
      base,
      selected: activeCandidate.selected,
      selectedIndex: activeCandidate.selectedIndex,
      activeMode,
      availability,
      display: activeCandidate.selected.sizeLabel,
      applyValue: activeCandidate.selected.variantSizeValue,
      offsetApplied: activeCandidate.offset,
      modeLabel: waistModes[activeMode].label,
    };
  };

const buildLengthResult = (lengths, baseIndex, lengthMode, unit) => {
  if (!lengths.length || baseIndex < 0 || !lengths[baseIndex]) {
    return {
      base: null,
      selected: null,
      activeMode: 'regular',
      availability: {
        cropped: false,
        regular: false,
        stacked: false,
      },
      display: '--',
      applyValue: '',
      copyKey: 'hemRegularCopy',
    };
  }

  const base = lengths[baseIndex];

  /*
   * Le lunghezze sono ordinate in modo decrescente:
   * indice 0 = più lunga
   * ultimo indice = più corta
   */
  const longerOne = lengths[baseIndex - 1] || null;
  const shorterTwo = lengths[baseIndex + 2] || null;

  let activeMode = 'regular';
  let selected = base;

  if (lengthMode === 'cropped' && shorterTwo) {
    activeMode = 'cropped';
    selected = shorterTwo;
  } else if (lengthMode === 'stacked' && longerOne) {
    activeMode = 'stacked';
    selected = longerOne;
  }

  return {
    base,
    selected,
    activeMode,
    availability: {
      cropped: !!shorterTwo,
      regular: true,
      stacked: !!longerOne,
    },
    display: selected.lengthLabel,
    applyValue:
      selected.inseamCm !== null
        ? String(selected.inseamCm)
        : '',
    copyKey:
      activeMode === 'cropped'
        ? 'hemCroppedCopy'
        : activeMode === 'stacked'
          ? 'hemStackedCopy'
          : 'hemRegularCopy',
  };
};

  const enforceToggleAvailability = (predictor, recommendation) => {
    const fitGroup = predictor.querySelector('[data-klsh-fit-mode]');
    const lengthGroup = predictor.querySelector('[data-klsh-length-mode]');

    if (fitGroup) {
      fitGroup.querySelectorAll('.klsh-fit-toggle__button').forEach((button) => {
        const value = normalizeWaistMode(button.dataset.value, '');
        const disabled = value ? !recommendation?.waist?.availability?.[value] : false;
        setToggleButtonDisabled(button, disabled);
      });

      const activeFitMode = normalizeWaistMode(getActiveMode(fitGroup), 'true');
      if (!recommendation?.waist?.availability?.[activeFitMode]) {
        setActiveMode(fitGroup, recommendation?.waist?.activeMode || 'true');
      }
    }

    if (lengthGroup) {
      lengthGroup.querySelectorAll('.klsh-fit-toggle__button').forEach((button) => {
        const value = button.dataset.value;
        const disabled =
          value === 'cropped'
            ? !recommendation?.length?.availability?.cropped
            : value === 'stacked'
              ? !recommendation?.length?.availability?.stacked
              : false;
        setToggleButtonDisabled(button, disabled);
      });

      const activeLengthMode = getActiveMode(lengthGroup);
      if (activeLengthMode === 'cropped' && !recommendation?.length?.availability?.cropped) {
        setActiveMode(lengthGroup, 'regular');
      }
      if (activeLengthMode === 'stacked' && !recommendation?.length?.availability?.stacked) {
        setActiveMode(lengthGroup, 'regular');
      }
    }
  };

const getVisualUrlForView = (
  predictor,
  viewMode
) => {
  const fitMode = getActiveMode(
    predictor.querySelector(
      '[data-klsh-fit-mode]'
    )
  );

  const lengthMode = getActiveMode(
    predictor.querySelector(
      '[data-klsh-length-mode]'
    )
  );

  const normalizedViewMode =
    normalizeToken(viewMode);

  if (!normalizedViewMode) return '';

  const map = getVisualMap(predictor);

  return (
    map?.[fitMode]?.[lengthMode]?.[
      normalizedViewMode
    ] ||
    map?.[fitMode]?.regular?.[
      normalizedViewMode
    ] ||
    map?.true?.[lengthMode]?.[
      normalizedViewMode
    ] ||
    map?.true?.regular?.[
      normalizedViewMode
    ] ||
    map?.loose?.[lengthMode]?.[
      normalizedViewMode
    ] ||
    map?.loose?.regular?.[
      normalizedViewMode
    ] ||
    ''
  );
};

  const getVisualUrl = (predictor) => {
    const viewMode = getActiveMode(
      predictor.querySelector(
        '[data-klsh-view-mode]'
      )
    );

    return getVisualUrlForView(
      predictor,
      viewMode
    );
  };

  const renderMobileVisual = (
    predictor,
    viewMode
  ) => {
    const card = predictor.querySelector(
      `[data-klsh-fit-mobile-view="${viewMode}"]`
    );

    if (!card) return;

    const media = card.querySelector(
      '.klsh-fit-mobile-visual__media'
    );

    if (!media) return;

    const url = getVisualUrlForView(
      predictor,
      viewMode
    );

    let image = media.querySelector(
      `[data-klsh-fit-mobile-image="${viewMode}"]`
    );

    let placeholder = media.querySelector(
      '.klsh-fit-mobile-visual__placeholder'
    );

    if (url) {
      if (!image) {
        image = document.createElement('img');

        image.className =
          'klsh-fit-mobile-visual__image';

        image.width = 600;
        image.height = 800;
        image.loading = 'lazy';
        image.decoding = 'async';

        image.alt =
          card
            .querySelector(
              '.klsh-fit-mobile-visual__label'
            )
            ?.textContent?.trim() || '';

        image.setAttribute(
          'data-klsh-fit-mobile-image',
          viewMode
        );

        media.appendChild(image);
      }

      if (image.getAttribute('src') !== url) {
        image.setAttribute('src', url);
      }

      image.hidden = false;

      if (placeholder) {
        placeholder.hidden = true;
      }

      return;
    }

    if (image) {
      image.hidden = true;
    }

    if (!placeholder) {
      placeholder =
        document.createElement('div');

      placeholder.className =
        'klsh-fit-mobile-visual__placeholder';

      placeholder.textContent =
        getCopyMap(
          predictor
        ).visualPlaceholder;

      media.appendChild(placeholder);
    }

    placeholder.hidden = false;
  };

  const renderMobileVisuals = (predictor) => {
    ['front', 'back'].forEach((viewMode) => {
      renderMobileVisual(
        predictor,
        viewMode
      );
    });
  };

  const renderVisual = (predictor) => {
    renderMobileVisuals(predictor);

    const media = predictor.querySelector('[data-klsh-fit-visual-media]');
    if (!media) return;

    const url = getVisualUrl(predictor);
    let image = predictor.querySelector('[data-klsh-fit-visual-image]');
    let placeholder = predictor.querySelector('[data-klsh-fit-visual-placeholder]');

    if (url) {
      if (!image) {
        image = document.createElement('img');
        image.className = 'klsh-fit-visual__image';
        image.loading = 'lazy';
        image.alt = '';
        image.setAttribute('data-klsh-fit-visual-image', '');
        media.appendChild(image);
      }

      image.src = url;
      image.hidden = false;

      if (placeholder) placeholder.hidden = true;
      return;
    }

    if (image) image.hidden = true;

    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'klsh-fit-visual__placeholder';
      placeholder.setAttribute('data-klsh-fit-visual-placeholder', '');
      placeholder.textContent = getCopyMap(predictor).visualPlaceholder;
      media.appendChild(placeholder);
    }

    placeholder.hidden = false;
  };

  const computeRecommendation = (predictor) => {
    const profile = getMeasureProfile(predictor);
    const waistInput = predictor.querySelector('[data-klsh-fit-waist]');
    const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');
    const fitGroup = predictor.querySelector('[data-klsh-fit-mode]');
    const lengthGroup = predictor.querySelector('[data-klsh-length-mode]');

    const inputUnit = getSelectedInputUnit(predictor);
    const fitMode = getActiveMode(fitGroup);
    const requestedLengthMode = getActiveMode(lengthGroup);

    const waistValueCm = toCm(waistInput?.value, inputUnit);
    const inseamValueCm = toCm(inseamInput?.value, inputUnit);

    if (waistValueCm === null || !profile.sizes.length) return null;
    if (profileHasLengthChoices(profile) && inseamValueCm === null) return null;

    const copy = getCopyMap(predictor);

    /* La misura del cliente determina la label nominale W */
    const waistValueIn = waistValueCm / CM_PER_INCH;

    const baseWaistIndex = getNearestItemIndex(
      profile.sizes,
      'nominalWaist',
      waistValueIn,
      0
    );
    const hasLengthChoices = profileHasLengthChoices(profile);
    const defaultLengthIndex = hasLengthChoices ? profile.lengths.length - 1 : 0;
    const baseLengthIndex = hasLengthChoices
      ? getNearestItemIndex(profile.lengths, 'inseamCm', inseamValueCm, defaultLengthIndex)
      : -1;

    const waist = buildWaistResult(profile.sizes, baseWaistIndex, fitMode, profile);
    const length = buildLengthResult(profile.lengths, baseLengthIndex, requestedLengthMode, inputUnit);
    const activeLengthMode = length.selected ? length.activeMode : 'regular';

    return {
      inputUnit,
      waist,
      length,
      recommendedWaist: waist.display,
      recommendedHem: length.display,
      recommendedWaistValue: waist.applyValue,
      recommendedLengthValue: length.applyValue,
      hasHemRecommendation: !!length.selected,
      summary: applyTemplate(copy.summaryTemplate, {
        waist: waist.display,
        inseam: length.display,
      }),
      waistCopy: buildWaistCopy(copy, waist),
      hemCopy: copy[length.copyKey] || '',
      fitAvailability: waist.availability,
      lengthAvailability: length.availability,
      activeFitMode: waist.activeMode,
      activeLengthMode,
    };
  };

  const renderRecommendation = (predictor, recommendation) => {
    const result = predictor.querySelector('[data-klsh-fit-result]');
    const applyButton = predictor.querySelector('[data-klsh-fit-apply]');
    const hasLengthChoices = profileHasLengthChoices(getMeasureProfile(predictor));

    if (!result || !applyButton) return;

    const setText = (selector, value) => {
      const node = predictor.querySelector(selector);
      if (node) node.textContent = value;
    };

    const updateResultLook = () => {
  const fitLabel = predictor
    .querySelector(
      '[data-klsh-fit-mode] ' +
      '.klsh-fit-toggle__button.is-active ' +
      '.klsh-fit-toggle__label'
    )
    ?.textContent
    ?.trim();

  const lengthLabel = hasLengthChoices
    ? predictor
        .querySelector(
          '[data-klsh-length-mode] ' +
          '.klsh-fit-toggle__button.is-active ' +
          '.klsh-fit-toggle__label'
        )
        ?.textContent
        ?.trim()
    : '';

  const lookLabel = [
    fitLabel,
    lengthLabel,
  ]
    .filter(Boolean)
    .map((label) => label.toUpperCase())
    .join(' / ');

  setText(
    '[data-klsh-result-look]',
    lookLabel || '--'
  );
};

updateResultLook();

    const inseamValueNode = predictor.querySelector('[data-klsh-result-hem]');
    const inseamLine =
      inseamValueNode?.closest?.('.klsh-fit-predictor__output-field') ||
      inseamValueNode?.parentElement ||
      null;

    const divider =
      predictor.querySelector('.klsh-fit-predictor__result-divider') ||
      predictor.querySelector('.klsh-fit-predictor__output-separator') ||
      null;

    const inseamCopyNode = predictor.querySelector('[data-klsh-result-hem-copy]');

    if (!recommendation) {
      if (inseamLine) inseamLine.hidden = !hasLengthChoices;
      if (divider) divider.hidden = !hasLengthChoices;
      if (inseamCopyNode) inseamCopyNode.hidden = !hasLengthChoices;

      const defaultEmptySummary = hasLengthChoices
        ? 'Enter your waist and inseam to unlock the recommendation.'
        : 'Enter your waist to unlock the recommendation.';

      setText(
        '[data-klsh-result-summary]',
        predictor.dataset.klshFitEmptySummary || defaultEmptySummary
      );
      setText('[data-klsh-result-waist]', '--');
      setText('[data-klsh-result-hem]', '--');
      setText('[data-klsh-result-waist-copy]', '');
      setText('[data-klsh-result-hem-copy]', '');

      result.hidden = false;
      setPredictorLockedState(predictor, true);
      predictor.klshFitRecommendation = null;
      return;
    }

    setPredictorLockedState(predictor, false);

    enforceToggleAvailability(predictor, recommendation);

    updateResultLook();

    setText('[data-klsh-result-waist]', recommendation.recommendedWaist);
    setText('[data-klsh-result-summary]', recommendation.summary);
    setText('[data-klsh-result-waist-copy]', recommendation.waistCopy);

    const hasLength = recommendation.hasHemRecommendation;
    if (inseamLine) inseamLine.hidden = !hasLength;
    if (divider) divider.hidden = !hasLength;
    if (inseamCopyNode) inseamCopyNode.hidden = !hasLength;

    if (hasLength) {
      setText('[data-klsh-result-hem]', recommendation.recommendedHem);
      setText('[data-klsh-result-hem-copy]', recommendation.hemCopy);
    } else {
      setText('[data-klsh-result-hem]', '--');
      setText('[data-klsh-result-hem-copy]', '');
    }

    result.hidden = false;
    applyButton.disabled = false;
    predictor.klshFitRecommendation = recommendation;
  };

  const syncPredictorUi = (predictor) => {
    const profile = getMeasureProfile(predictor);
    const lengthMode = predictor.querySelector('[data-klsh-length-mode]');
    const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');

      const hasLengthChoices = profileHasLengthChoices(profile);
      const lengthChoice = lengthMode?.closest('.klsh-fit-predictor__choice');
      const inseamField = inseamInput?.closest('.klsh-fit-predictor__field');

      predictor.classList.toggle('klsh-fit-predictor--waist-only', !hasLengthChoices);

      if (!hasLengthChoices && lengthMode) {
        setActiveMode(lengthMode, 'regular');
      }

      if (lengthChoice) {
        lengthChoice.hidden = !hasLengthChoices;
      }

      if (inseamField) {
        inseamField.hidden = !hasLengthChoices;
      }
  };

  const updatePredictor = (predictor) => {
    updateUnitLabels(predictor);
    syncPredictorUi(predictor);
    const recommendation = computeRecommendation(predictor);

    if (recommendation?.activeFitMode && predictor.querySelector('[data-klsh-fit-mode]')) {
      setActiveMode(predictor.querySelector('[data-klsh-fit-mode]'), recommendation.activeFitMode);
    }

    if (recommendation?.activeLengthMode && predictor.querySelector('[data-klsh-length-mode]')) {
      setActiveMode(predictor.querySelector('[data-klsh-length-mode]'), recommendation.activeLengthMode);
    }

    renderVisual(predictor);
    renderRecommendation(predictor, recommendation);
    return recommendation;
  };

  const submitProductForm = (predictor) => {
    const form = getProductForm(predictor);
    if (!form) return false;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return true;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.click();
      return true;
    }

    form.submit();
    return true;
  };

  const getVariantIdInput = (predictor) => {
    const form = getProductForm(predictor);
    if (!form) return null;
    return form.querySelector('input[name="id"]');
  };

  const waitForVariantIdUpdate = (
    predictor,
    previousId,
    timeoutMs = 2000
  ) => {
    return new Promise((resolve) => {
      const start = Date.now();

      const tick = () => {
        const input = getVariantIdInput(predictor);

        if (
          input?.value &&
          input.value !== previousId
        ) {
          resolve(true);
          return;
        }

        if (Date.now() - start > timeoutMs) {
          resolve(false);
          return;
        }

        window.setTimeout(tick, 50);
      };

      tick();
    });
  };

  const applyRecommendation = async (predictor) => {
    clampPredictorInputs(predictor);

    const recommendation = updatePredictor(predictor);

    if (!recommendation) return;

    const applyButton =
      predictor.querySelector('[data-klsh-fit-apply]');

    if (
      applyButton?.dataset?.klshFitSubmitting === 'true'
    ) {
      return;
    }

    const unlockApplyButton = () => {
      if (!applyButton) return;

      applyButton.dataset.klshFitSubmitting = 'false';
      applyButton.disabled = false;
      applyButton.removeAttribute('aria-busy');
    };

    if (applyButton) {
      applyButton.dataset.klshFitSubmitting = 'true';
      applyButton.disabled = true;
      applyButton.setAttribute('aria-busy', 'true');
    }

    const recommendedWaistValue =
      recommendation.recommendedWaistValue;

    const recommendedLengthValue =
      recommendation.recommendedLengthValue;

    const waistAlreadySelected = isWaistSelected(
      predictor,
      recommendedWaistValue
    );

    const previousVariantId =
      getVariantIdInput(predictor)?.value || '';

    const waistApplied = selectWaist(
      predictor,
      recommendedWaistValue
    );

    if (!waistApplied) {
      unlockApplyButton();
      return;
    }

    if (!waistAlreadySelected) {
      const variantUpdated =
        await waitForVariantIdUpdate(
          predictor,
          previousVariantId,
          2000
        );

      if (!variantUpdated) {
        unlockApplyButton();
        return;
      }
    }

    if (
      recommendation.hasHemRecommendation &&
      recommendedLengthValue
    ) {
      const hemApplied =
        await waitForHemSelection(
          predictor,
          recommendedLengthValue,
          2500
        );

      if (!hemApplied) {
        unlockApplyButton();
        return;
      }
    }

    const submitted = submitProductForm(predictor);

    if (!submitted) {
      unlockApplyButton();
      return;
    }

    window.setTimeout(unlockApplyButton, 350);
  };

  const bindUnitToggleGroup = (
    group,
    predictor
  ) => {
    if (!group) return;

    group
      .querySelectorAll('.klsh-fit-toggle__button')
      .forEach((button) => {
        button.addEventListener('click', () => {
          if (
            button.disabled ||
            button.getAttribute('aria-disabled') === 'true'
          ) {
            return;
          }

          const previousUnit =
            getSelectedInputUnit(predictor);

          const nextUnit =
            normalizeToken(button.dataset.value);

          if (!['cm', 'in'].includes(nextUnit)) {
            return;
          }

          convertPredictorInputsUnit(
            predictor,
            previousUnit,
            nextUnit
          );

          setActiveMode(group, nextUnit);
          clampPredictorInputs(predictor);
          updatePredictor(predictor);
        });
      });
  };

  const bindToggleGroup = (group, predictor) => {
    if (!group) return;

    group.querySelectorAll('.klsh-fit-toggle__button').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        setActiveMode(group, button.dataset.value);
        updatePredictor(predictor);
      });
    });
  };

  const bindOpenRefresh = (predictor) => {
    const modal = predictor.closest('modal-dialog');
    const legacyDrawer = predictor.closest('.klsh-fit-drawer');
    const id = modal?.id || legacyDrawer?.id;

    if (!id) return;

    document.querySelectorAll(`[data-modal="#${id}"]`).forEach((opener) => {
      opener.addEventListener('click', () => {
        window.setTimeout(() => {
          resetPredictorState(predictor);
          updatePredictor(predictor);
        }, 0);
      });
    });
  };

  const initPredictor = (predictor) => {
    if (!predictor || predictor.dataset.klshFitPredictorInitialized === 'true') return;
    predictor.dataset.klshFitPredictorInitialized = 'true';

    const waistInput = predictor.querySelector('[data-klsh-fit-waist]');
    const inseamInput = predictor.querySelector('[data-klsh-fit-inseam]');
    const fitMode = predictor.querySelector('[data-klsh-fit-mode]');
    const lengthMode = predictor.querySelector('[data-klsh-length-mode]');
    const viewMode = predictor.querySelector('[data-klsh-view-mode]');
    const unitMode = predictor.querySelector('[data-klsh-fit-unit-mode]');
    const applyButton = predictor.querySelector('[data-klsh-fit-apply]');

    predictor.dataset.klshFitEmptySummary =
      predictor.querySelector('[data-klsh-result-summary]')?.textContent?.trim() || '';

    resetPredictorState(predictor);
    updatePredictor(predictor);

    [waistInput, inseamInput].forEach((input) => {
      if (!input) return;

      input.addEventListener('input', () => updatePredictor(predictor));

      input.addEventListener('change', () => {
        clampPredictorInputs(predictor);
        updatePredictor(predictor);
      });

      input.addEventListener('blur', () => {
        clampPredictorInputs(predictor);
        updatePredictor(predictor);
      });
    });
    
    bindUnitToggleGroup(unitMode, predictor);
    bindToggleGroup(fitMode, predictor);
    bindToggleGroup(lengthMode, predictor);
    bindToggleGroup(viewMode, predictor);

    if (applyButton) {
      applyButton.addEventListener('click', () => applyRecommendation(predictor));
    }

    bindOpenRefresh(predictor);
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-klsh-fit-predictor]').forEach(initPredictor);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();